import axios from "axios";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  hasRole,
  NoUserFound,
  getOrdinalSuffix,
} from "../../../components/Utils";
import Loader from "../../../components/common/Loader";
import MemberDetailsModal from "../../../components/common/MemberDetailsModal";
import { USER_EDIT } from "../../../routes/routes";

const rankColors = [
  "#183f2a", // 1st
  "#225338", // 2nd
  "#2d6747", // 3rd
  "#3b7c57", // 4th
  "#4b9268", // 5th
  "#5ca77a", // 6th
  "#70bd8d", // 7th
  "#8ed1aa", // 8th
  "#bce2cc", // 9th
  "#e3e9e5", // 10th+
];
const getRankColor = (index) => rankColors[Math.min(index, 9)];
const getRankTextColor = (index) => (index >= 6 ? "#1A4331" : "#ffffff");

const ValueBadge = ({ value }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: "16px",
        fontSize: "0.9125rem",
        fontWeight: 600,
        // background: "#ecfdf3",
        color: "#000",
        // border: "1px solid #a6f4c5",
      }}
    >
      {value}
    </span>
  );
};

const RenderTableBlock = ({
  title,
  exportPerm,
  pdfKey,
  dataList,
  valueKey,
  valueLabel,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
  kingTitle,
}) => {
  const top3Users = dataList?.slice(0, 3) || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [detailsModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [modalLoading, setModalLoading] = useState(false);

  const handleViewProfile = async (userId) => {
    if (!userId) return;

    setDetailModal(true);
    setModalLoading(true);
    setDetailData({});

    try {
      const res = await axios.get(USER_EDIT + userId);
      if (res.status === 200 && res.data.success) {
        setDetailData(res.data.data);
      } else {
        toast.error(res.data.message || "Failed to load member details");
        setDetailModal(false);
      }
    } catch {
      toast.error("Failed to load member details");
      setDetailModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const minSwipeDistance = 40;

  // Reset active index when tab changes
  useEffect(() => {
    setActiveIndex(0);
  }, [title]);

  const handleStart = (e) => {
    setIsDragging(true);
    setTouchEnd(null);
    setTouchStart(e.type.includes("mouse") ? e.pageX : e.touches[0].clientX);
  };

  const handleMove = (e) => {
    if (!isDragging && !e.type.includes("touch")) return;
    setTouchEnd(e.type.includes("mouse") ? e.pageX : e.touches[0].clientX);
  };

  const handleEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      // Swipe left - next card
      setActiveIndex((prev) => (prev + 1) % top3Users.length);
    } else if (distance < -minSwipeDistance) {
      // Swipe right - previous card
      setActiveIndex(
        (prev) => (prev - 1 + top3Users.length) % top3Users.length,
      );
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const getCardStyle = (pos) => {
    const base = {
      userSelect: "none",
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      cursor: isDragging ? "grabbing" : "grab",
      margin: 0,
    };

    if (pos === 0) {
      return {
        ...base,
        position: "relative",
        zIndex: 3,
        transform: "none",
        opacity: 1,
        boxShadow: "0 20px 40px rgba(10, 30, 20, 0.5)",
        pointerEvents: "auto",
      };
    } else if (pos === 1) {
      return {
        ...base,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 2,
        transform: "translateX(15px) translateY(6px) rotate(4deg) scale(0.95)",
        opacity: 1,
        filter: "brightness(0.85)",
        boxShadow: "0 8px 20px rgba(10, 30, 20, 0.4)",
        pointerEvents: "auto",
      };
    } else {
      return {
        ...base,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1,
        transform:
          "translateX(-15px) translateY(6px) rotate(-4deg) scale(0.95)",
        opacity: 1,
        filter: "brightness(0.85)",
        boxShadow: "0 8px 20px rgba(10, 30, 20, 0.4)",
        pointerEvents: "auto",
      };
    }
  };

  return (
    <div className="row">
      <div className="col-12 col-xl-7 mb-4 mb-xl-0">
        <div className="modern-table-wrapper">
          <div className="modern-card-header">
            <h5 className="d-flex align-items-center">
              <i
                className="bx bxs-trophy me-2"
                style={{
                  color: "#8e7c13ff",
                  fontSize: "1.5rem",
                  filter: "drop-shadow(0px 2px 2px rgba(255, 215, 0, 0.4))",
                }}
              ></i>
              {title}
            </h5>
            {hasPermission(exportPerm) && dataList?.length > 0 && (
              <button
                className="btn-modern-download"
                title="Download PDF"
                onClick={() => {
                  if (!pdfLoading) handleDownloadPDF(pdfKey);
                }}
                disabled={pdfLoading}
              >
                <i
                  className={`bx bx-cloud-download ${pdfLoading ? "bx-fade-down" : ""}`}
                ></i>
                Download PDF
              </button>
            )}
          </div>
          <div className="rich-list-wrapper">
            {loading ? (
              <div style={{ padding: "40px" }}>
                <Loader loading={loading} />
              </div>
            ) : dataList?.length > 0 ? (
              dataList.map((users, i) => (
                <div className="rich-list-item" key={i}>
                  <div
                    className="rank-badge"
                    style={{
                      width: "60px",
                      minWidth: "60px",
                      height: "44px",
                      background: getRankColor(i),
                      color: getRankTextColor(i),
                      fontWeight: "700",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "-24px",
                      marginRight: "40px",
                      paddingRight: "10px",
                      clipPath:
                        "polygon(0 0, 80% 0, 100% 50%, 80% 100%, 0 100%)",
                    }}
                  >
                    {getOrdinalSuffix(i + 1)}
                  </div>
                  <img
                    src={users?.thumb_profile_image || users?.profile_image}
                    alt="profile"
                    className="rich-item-img"
                  />
                  <div className="rich-item-info">
                    <span className="rich-item-name">{users?.name}</span>
                    <span className="rich-item-sub">
                      {users?.user_company_name ||
                        users?.company_name ||
                        "Member"}
                    </span>
                  </div>
                  {/* <div className="rich-item-location">
                    {users?.chapter_name && (
                      <span className="rich-item-loc-main">
                        {users.chapter_name}
                      </span>
                    )}
                    {users?.region_name && (
                      <span className="rich-item-loc-sub">
                        {users.region_name}
                        {users?.state_name ? `, ${users.state_name}` : ""}
                      </span>
                    )}
                    {!users?.region_name && users?.state_name && (
                      <span className="rich-item-loc-sub">
                        {users.state_name}
                      </span>
                    )}
                  </div> */}
                  <div className="rich-item-value">
                    <ValueBadge value={users?.[valueKey]} />
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: "64px 24px", textAlign: "center" }}>
                <NoUserFound
                  title={`No ${title.replace("Top 10 ", "")} Found`}
                  subtitle="Everything is on track! There are no records to display at this time."
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-xl-5">
        {top3Users.length > 0 && (
          <div
            className="top-performer-stack"
            style={{
              position: "relative",
              perspective: "1000px",
              touchAction: "pan-y",
              margin:
                top3Users.length === 1
                  ? "72px 16px 16px 16px"
                  : "72px auto 40px auto",
              maxWidth: top3Users.length === 1 ? "100%" : "340px",
            }}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
          >
            {top3Users.map((topUser, index) => {
              const pos =
                (index - activeIndex + top3Users.length) % top3Users.length;
              return (
                <div
                  key={index}
                  className={`top-performer-card${pos !== 0 ? " top-performer-card--back" : ""}`}
                  onClick={() => setActiveIndex(index)}
                  style={{
                    ...getCardStyle(pos),
                    transformOrigin: "bottom center",
                    transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  }}
                >
                  <div className="top-performer-header">
                    {index === 0 && (
                      <span className="badge-star">
                        <i
                          className="bx bxs-crown"
                          style={{ color: "#FFD700", marginRight: "4px" }}
                        ></i>
                        {kingTitle || "Star Performer"}
                      </span>
                    )}
                  </div>

                  <div className="top-performer-content">
                    <div className="watermark-bg">{getOrdinal(index + 1)}</div>

                    <div className="performer-layout">
                      <div className="performer-stats">
                        <div className="p-stat">
                          <span className="p-stat-label">
                            {valueLabel.toUpperCase()}
                          </span>
                          <span className="p-stat-val">
                            {topUser[valueKey] || 0}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">ROLE</span>
                          <span className="p-stat-val-small">
                            {topUser.user_role_name ||
                              topUser.role_name ||
                              "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">CONTACT</span>
                          <span className="p-stat-val-small">
                            {topUser.mobile_number || "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">CATEGORY</span>
                          <span className="p-stat-val-small">
                            {topUser.category_name || "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">COMPANY</span>
                          <span className="p-stat-val-small">
                            {topUser.user_company_name ||
                              topUser.company_name ||
                              "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="performer-image-container">
                        <div className="performer-img-wrapper">
                          <img
                            src={
                              topUser.profile_image ||
                              topUser.from_profile_image ||
                              topUser.user_profile_image ||
                              "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80"
                            }
                            alt="performer"
                            className="performer-img"
                          />
                        </div>
                        <div className="performer-glow"></div>
                      </div>
                    </div>
                  </div>

                  <div className="top-performer-footer">
                    <div className="performer-info">
                      <h4 className="performer-name">
                        {topUser.name ||
                          topUser.from_user_name ||
                          topUser.user_name}
                      </h4>
                      <p className="performer-chapter">
                        {topUser.chapter_name || "N/A"},{" "}
                        {topUser.region_name || "N/A"}
                      </p>
                    </div>
                    <button
                      className="btn-view-profile"
                      style={{ visibility: pos === 0 ? "visible" : "hidden" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewProfile(
                          topUser.id || topUser.from_user_id || topUser.user_id,
                        );
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MemberDetailsModal
        show={detailsModal}
        onHide={() => setDetailModal(false)}
        detailData={detailData}
        loading={modalLoading}
      />
    </div>
  );
};

const TopTenTables = ({
  data,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
}) => {
  const tabs = [
    {
      id: "points",
      label: "Points",
      icon: "bx bx-medal",
      perm: "dashboard-points-data-view",
    },
    {
      id: "rfls",
      label: "RFLs Given",
      icon: "bx bx-share-alt",
      perm: "dashboard-rfls-data-view",
    },
    {
      id: "tyns",
      label: "Business Given",
      icon: "bx bx-briefcase",
      perm: "dashboard-tyns-data-view",
    },
    {
      id: "fbms",
      label: "FBMs",
      icon: "bx bx-conversation",
      perm: "dashboard-fbms-data-view",
    },
    {
      id: "visitors",
      label: "Visitors Given",
      icon: "bx bx-group",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "chief_guests",
      label: "Chief Guests",
      icon: "bx bx-star",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "power_dates",
      label: "Power Dates",
      icon: "bx bx-calendar-star",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "inductions",
      label: "Inductees",
      icon: "bx bx-user-check",
      perm: "dashboard-visitors-data-view", // Existing perm used for inductees
    },
  ];

  const tabConfig = {
    points: {
      title: "Top 10 Performing Members",
      exportPerm: "dashboard-points-data-view-export",
      pdfKey: "top_10_points_members",
      dataList: data.top10PointsUsersList,
      valueKey: "points",
      valueLabel: "Points",
      kingTitle: "King of Performers",
    },
    tyns: {
      title: "Top 10 Business Given Members",
      exportPerm: "dashboard-tyns-data-view-export",
      pdfKey: "top_10_business_given_members",
      dataList: data.top10BusinessUsersList,
      valueKey: "business_value",
      valueLabel: "Amount",
      kingTitle: "King of TYNs",
    },
    rfls: {
      title: "Top 10 RFLs Given Members",
      exportPerm: "dashboard-rfls-data-view-export",
      pdfKey: "top_10_referral_given_members",
      dataList: data.top10ReferralsGivenUsersList,
      valueKey: "referrals_count",
      valueLabel: "Count",
      kingTitle: "King of RFLs",
    },
    visitors: {
      title: "Top 10 Visitors Given Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_visitors_members",
      dataList: data.top10VisitorsGivenUsersList,
      valueKey: "visitors_count",
      valueLabel: "Count",
      kingTitle: "King of Visitors",
    },
    fbms: {
      title: "Top 10 FBMs By Members",
      exportPerm: "dashboard-fbms-data-view-export",
      pdfKey: "top_10_121_members",
      dataList: data.top10121sGivenUsersList,
      valueKey: "one2one_count",
      valueLabel: "Count",
      kingTitle: "King of FBMs",
    },
    inductions: {
      title: "Top 10 Inductees Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_inductions_members",
      dataList: data.top10InductionsGivenUsersList,
      valueKey: "inductions_count",
      valueLabel: "Count",
      kingTitle: "King of Inductions",
    },
    chief_guests: {
      title: "Top 10 Chief Guests Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_chief_guests_members",
      dataList: data.top10ChiefGuestGivenUsersList,
      valueKey: "chief_guests_count",
      valueLabel: "Count",
      kingTitle: "King of Chief Guests",
    },
    power_dates: {
      title: "Top 10 Power Dates Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_power_dates_members",
      dataList: data.top10PowerDateGivenUsersList,
      valueKey: "power_dates_count",
      valueLabel: "Count",
      kingTitle: "King of Power Dates",
    },
  };

  const allowedTabs = tabs.filter((tab) => hasPermission(tab.perm));
  const [activeTab, setActiveTab] = useState(
    allowedTabs.length > 0 ? allowedTabs[0].id : "",
  );

  useEffect(() => {
    if (
      allowedTabs.length > 0 &&
      !allowedTabs.find((t) => t.id === activeTab)
    ) {
      setActiveTab(allowedTabs[0].id);
    }
  }, [hasPermission]);

  if (allowedTabs.length === 0) return null;

  const config = tabConfig[activeTab];

  return (
    <>
      <div className="row dash-table mb-5 pb-5">
        <div className="col-12">
          <div className="card modern-card mb-4">
            <div className="modern-tabs-container">
              {allowedTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`modern-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </div>
              ))}
            </div>

            {config && (
              <RenderTableBlock
                {...config}
                loading={loading}
                hasPermission={hasPermission}
                pdfLoading={pdfLoading}
                handleDownloadPDF={handleDownloadPDF}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopTenTables;
